import { auth, db, functions } from '@/config/firebase'
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { fcmTokenService } from '@/services/FCMTokenService'

class FCMDiagnostic {
  async runFullDiagnostic(): Promise<any> {
    console.log('🔍 Starting FCM Full Diagnostic...')
    
    const results = {
      environment: {} as any,
      user: null as any,
      tokens: [] as any[],
      permission: null as string | null,
      serviceWorker: null as any,
      cloudFunction: null as any,
      scheduledFunction: null as any,
      testResult: null as any,
      issues: [] as string[]
    }

    // 1. Check environment and configuration
    console.log('1️⃣ Checking environment and configuration...')
    results.environment = {
      isHttps: location.protocol === 'https:',
      isLocalhost: location.hostname === 'localhost',
      hasNotifications: 'Notification' in window,
      hasServiceWorker: 'serviceWorker' in navigator,
      hasPushManager: 'PushManager' in window,
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY ? 
        `${import.meta.env.VITE_FIREBASE_VAPID_KEY.substring(0, 20)}...` : 
        'NOT_FOUND',
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID
    }

    console.log('🌍 Environment check:', results.environment)

    // Check for critical issues
    if (!results.environment.isHttps && !results.environment.isLocalhost) {
      results.issues.push('❌ HTTPS required for FCM (except localhost)')
    }
    if (!results.environment.hasNotifications) {
      results.issues.push('❌ Notifications not supported in this browser')
    }
    if (!results.environment.hasServiceWorker) {
      results.issues.push('❌ Service Worker not supported in this browser')
    }
    if (!results.environment.hasPushManager) {
      results.issues.push('❌ Push Manager not supported in this browser')
    }
    if (results.environment.vapidKey === 'NOT_FOUND') {
      results.issues.push('❌ VAPID key not configured - check VITE_FIREBASE_VAPID_KEY in .env')
    }

    // 2. Check user authentication
    console.log('2️⃣ Checking user authentication...')
    results.user = auth.currentUser
    if (!results.user) {
      results.issues.push('❌ No authenticated user found')
      console.error('❌ No authenticated user found')
      return results
    }
    console.log('✅ User authenticated:', results.user.uid)

    // 3. Check notification permission
    console.log('3️⃣ Checking notification permission...')
    results.permission = Notification.permission
    console.log('📱 Notification permission:', results.permission)
    
    if (results.permission === 'denied') {
      results.issues.push('❌ Notification permission denied - user must enable in browser settings')
    } else if (results.permission === 'default') {
      results.issues.push('⚠️ Notification permission not requested yet')
    }

    // 4. Check FCM tokens
    console.log('4️⃣ Checking FCM tokens...')
    try {
      const tokensQuery = query(collection(db, 'users', results.user.uid, 'fcmTokens'))
      const tokensSnapshot = await getDocs(tokensQuery)
      results.tokens = tokensSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      console.log('📱 FCM Tokens found:', results.tokens.length)
      results.tokens.forEach(token => {
        console.log(`  - ${token.platform}: ${token.token?.substring(0, 20)}...`)
      })
      
      if (results.tokens.length === 0) {
        results.issues.push('❌ No FCM tokens found - token generation may have failed')
      }
    } catch (error) {
      results.issues.push(`❌ Error checking tokens: ${error}`)
      console.error('❌ Error checking tokens:', error)
    }

    // 5. Check service worker
    console.log('5️⃣ Checking service worker...')
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      results.serviceWorker = registrations.find(reg => 
        reg.active?.scriptURL.includes('firebase-messaging-sw.js')
      )
      console.log('⚙️ FCM Service Worker:', results.serviceWorker ? 'Found' : 'Not found')
      
      if (!results.serviceWorker) {
        results.issues.push('❌ FCM Service Worker not registered')
      }
    } else {
      results.issues.push('❌ Service Worker not supported')
    }

    // 6. Test cloud function
    console.log('6️⃣ Testing cloud function...')
    try {
      const sendFunction = httpsCallable(functions, 'sendNotification')
      const result = await sendFunction({
        userId: results.user.uid,
        notification: {
          title: '🔍 FCM Debug Test',
          body: 'Testing FCM functionality',
          data: {
            test: 'true',
            debug: 'true',
            timestamp: Date.now().toString()
          }
        }
      })
      results.cloudFunction = { success: true, data: result.data }
      console.log('✅ Cloud function call successful:', result.data)
    } catch (error: any) {
      results.cloudFunction = { success: false, error: error.message || error }
      results.issues.push(`❌ Cloud function failed: ${error.message || error}`)
      console.error('❌ Cloud function call failed:', error)
    }

    // 7. Test FCM token generation
    console.log('7️⃣ Testing FCM token generation...')
    try {
      const token = await fcmTokenService.getAndSaveToken()
      results.testResult = { 
        tokenGenerated: !!token,
        tokenPreview: token ? token.substring(0, 20) + '...' : null
      }
      console.log('🎫 FCM Token generation:', token ? 'Success' : 'Failed')
      
      if (!token) {
        results.issues.push('❌ FCM token generation failed')
      }
    } catch (error: any) {
      results.testResult = { tokenGenerated: false, error: error.message || error }
      results.issues.push(`❌ FCM Token generation error: ${error.message || error}`)
      console.error('❌ FCM Token generation failed:', error)
    }

    // 8. Test scheduled notification function
    console.log('8️⃣ Testing scheduled notification function...')
    try {
      const scheduleFunction = httpsCallable(functions, 'scheduleNotification')
      const scheduleResult = await scheduleFunction({
        userId: results.user.uid,
        notification: {
          title: '⏰ Scheduled Test',
          body: 'This is a scheduled notification test',
          scheduledTime: new Date(Date.now() + 60000).toISOString(), // 1 minute from now
          data: {
            test: 'scheduled',
            debug: 'true',
            timestamp: Date.now().toString()
          }
        }
      })
      results.scheduledFunction = { success: true, data: scheduleResult.data }
      console.log('✅ Scheduled notification function successful:', scheduleResult.data)
    } catch (error: any) {
      results.scheduledFunction = { success: false, error: error.message || error }
      results.issues.push(`❌ Scheduled notification function failed: ${error.message || error}`)
      console.error('❌ Scheduled notification function failed:', error)
    }

    // 9. Check browser notification support
    console.log('9️⃣ Testing browser notifications...')
    try {
      if (results.permission === 'granted') {
        const testNotification = new Notification('Test Notification', {
          body: 'This is a test notification',
          icon: '/logo-192x192.avif'
        })
        console.log('✅ Browser notifications working')
        testNotification.close()
      } else {
        console.log('⚠️ Cannot test browser notifications - permission not granted')
      }
    } catch (error: any) {
      results.issues.push(`❌ Browser notification test failed: ${error.message || error}`)
      console.error('❌ Browser notification test failed:', error)
    }

    // Summary
    console.log('\n📊 DIAGNOSTIC SUMMARY:')
    console.log('='.repeat(50))
    
    if (results.issues.length === 0) {
      console.log('✅ All checks passed! FCM should be working.')
    } else {
      console.log(`❌ Found ${results.issues.length} issues:`)
      results.issues.forEach(issue => console.log(`  ${issue}`))
    }

    console.log('\n🔧 RECOMMENDED FIXES:')
    console.log('='.repeat(50))
    
    if (results.issues.some(issue => issue.includes('VAPID key'))) {
      console.log('1. Add VAPID key to .env file:')
      console.log('   VITE_FIREBASE_VAPID_KEY=your_vapid_key_here')
      console.log('   Get it from Firebase Console → Project Settings → Cloud Messaging')
    }
    
    if (results.issues.some(issue => issue.includes('permission denied'))) {
      console.log('2. Enable notifications in browser settings:')
      console.log('   Chrome: chrome://settings/content/notifications')
      console.log('   Firefox: about:preferences#privacy → Permissions → Notifications')
    }
    
    if (results.issues.some(issue => issue.includes('No FCM tokens'))) {
      console.log('3. Request notification permission and regenerate tokens:')
      console.log('   await Notification.requestPermission()')
      console.log('   await fcmTokenService.getAndSaveToken()')
    }
    
    if (results.issues.some(issue => issue.includes('Cloud function failed'))) {
      console.log('4. Deploy cloud functions:')
      console.log('   cd functions && npm run deploy')
    }

    return results
  }

  async requestPermissionAndTest(): Promise<void> {
    console.log('🔔 Requesting notification permission...')
    
    try {
      const permission = await Notification.requestPermission()
      console.log('📱 Permission result:', permission)
      
      if (permission === 'granted') {
        console.log('✅ Permission granted! Testing FCM...')
        
        // Initialize FCM
        await fcmTokenService.initialize()
        
        // Get and save token
        const token = await fcmTokenService.getAndSaveToken()
        if (token) {
          console.log('✅ FCM token generated:', token.substring(0, 20) + '...')
          
          // Test notification
          const sendFunction = httpsCallable(functions, 'sendNotification')
          const result = await sendFunction({
            userId: auth.currentUser?.uid,
            notification: {
              title: '🎉 FCM Test Success!',
              body: 'Notifications are now working!',
              data: { test: 'success' }
            }
          })
          console.log('✅ Test notification sent:', result.data)
        } else {
          console.error('❌ Failed to generate FCM token')
        }
      } else {
        console.error('❌ Permission denied:', permission)
      }
    } catch (error) {
      console.error('❌ Error requesting permission:', error)
    }
  }

  async checkScheduledNotifications(): Promise<any> {
    console.log('🔍 Checking Scheduled Notifications...')
    
    const results = {
      scheduledNotifications: [] as any[],
      pendingCount: 0,
      dueCount: 0,
      issues: [] as string[]
    }

    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        results.issues.push('❌ No authenticated user')
        return results
      }

      // Check all scheduled notifications for this user
      const scheduledQuery = query(
        collection(db, 'scheduledNotifications'),
        where('userId', '==', currentUser.uid)
      )
      const scheduledSnapshot = await getDocs(scheduledQuery)
      
      results.scheduledNotifications = scheduledSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      console.log(`📅 Found ${results.scheduledNotifications.length} scheduled notifications for user`)

      // Check pending notifications
      const pendingQuery = query(
        collection(db, 'scheduledNotifications'),
        where('userId', '==', currentUser.uid),
        where('status', '==', 'pending')
      )
      const pendingSnapshot = await getDocs(pendingQuery)
      results.pendingCount = pendingSnapshot.size

      // Check which ones are due
      const now = new Date()
      const dueNotifications = results.scheduledNotifications.filter(notification => {
        if (notification.status !== 'pending') return false
        const scheduledTime = new Date(notification.scheduledTime)
        return scheduledTime <= now
      })
      results.dueCount = dueNotifications.length

      console.log(`⏰ Pending notifications: ${results.pendingCount}`)
      console.log(`🚀 Due notifications: ${results.dueCount}`)

      // Log details of each notification
      results.scheduledNotifications.forEach(notification => {
        const scheduledTime = new Date(notification.scheduledTime)
        const isDue = scheduledTime <= now
        console.log(`📋 ${notification.id}: ${notification.status} - ${scheduledTime.toISOString()} ${isDue ? '(DUE)' : '(NOT DUE)'}`)
      })

      if (results.dueCount > 0) {
        results.issues.push(`⚠️ ${results.dueCount} notifications are due but not processed`)
      }

      return results
    } catch (error: any) {
      results.issues.push(`❌ Error checking scheduled notifications: ${error.message || error}`)
      console.error('❌ Error checking scheduled notifications:', error)
      return results
    }
  }

  async compareDebugApproaches(): Promise<any> {
    console.log('🔍 Comparing FCM Debug Approaches...')
    
    const results = {
      directApproach: null as any,
      unifiedApproach: null as any,
      differences: [] as string[]
    }

    // Test direct approach (like /fcm-debug)
    console.log('1️⃣ Testing direct approach...')
    try {
      const sendFunction = httpsCallable(functions, 'sendNotification')
      const directResult = await sendFunction({
        userId: auth.currentUser?.uid,
        notification: {
          title: '🔧 Direct Test',
          body: 'Direct cloud function call',
          data: { approach: 'direct', timestamp: Date.now().toString() }
        }
      })
      results.directApproach = { success: true, data: directResult.data }
      console.log('✅ Direct approach successful')
    } catch (error: any) {
      results.directApproach = { success: false, error: error.message || error }
      console.error('❌ Direct approach failed:', error)
    }

    // Test unified approach (like /unified-debug-notifications)
    console.log('2️⃣ Testing unified approach...')
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        results.unifiedApproach = { success: false, error: 'No authenticated user' }
        results.differences.push('❌ Unified approach: No authenticated user')
        console.log('❌ Unified approach: No authenticated user')
        return results
      }

      // Simulate the unified approach by checking FCM tokens first
      const tokensQuery = query(collection(db, 'users', currentUser.uid, 'fcmTokens'))
      const tokensSnapshot = await getDocs(tokensQuery)
      
      if (tokensSnapshot.empty) {
        results.unifiedApproach = { success: false, error: 'No FCM tokens found' }
        results.differences.push('❌ Unified approach: No FCM tokens found')
        console.log('❌ Unified approach: No FCM tokens found')
      } else {
        const sendFunction = httpsCallable(functions, 'sendNotification')
        const unifiedResult = await sendFunction({
          userId: auth.currentUser?.uid,
          notification: {
            title: '🔄 Unified Test',
            body: 'Unified approach test',
            data: { approach: 'unified', timestamp: Date.now().toString() }
          }
        })
        results.unifiedApproach = { success: true, data: unifiedResult.data }
        console.log('✅ Unified approach successful')
      }
    } catch (error: any) {
      results.unifiedApproach = { success: false, error: error.message || error }
      console.error('❌ Unified approach failed:', error)
    }

    // Analyze differences
    if (results.directApproach?.success && !results.unifiedApproach?.success) {
      results.differences.push('⚠️ Direct approach works but unified approach fails')
    } else if (!results.directApproach?.success && results.unifiedApproach?.success) {
      results.differences.push('⚠️ Unified approach works but direct approach fails')
    } else if (results.directApproach?.success && results.unifiedApproach?.success) {
      results.differences.push('✅ Both approaches work correctly')
    } else {
      results.differences.push('❌ Both approaches fail')
    }

    console.log('📊 Comparison Results:', results)
    return results
  }
}

// Create global instance
const fcmDiagnostic = new FCMDiagnostic()

// Expose to window for console access
declare global {
  interface Window {
    fcmDiagnostic: {
      runFullDiagnostic: () => Promise<any>
      requestPermissionAndTest: () => Promise<void>
      compareDebugApproaches: () => Promise<any>
      checkScheduledNotifications: () => Promise<any>
    }
  }
}

window.fcmDiagnostic = {
  runFullDiagnostic: () => fcmDiagnostic.runFullDiagnostic(),
  requestPermissionAndTest: () => fcmDiagnostic.requestPermissionAndTest(),
  compareDebugApproaches: () => fcmDiagnostic.compareDebugApproaches(),
  checkScheduledNotifications: () => fcmDiagnostic.checkScheduledNotifications()
}

export default fcmDiagnostic 